/**
 * Keyboard and draft rules for the inline editors.
 *
 * The shortcuts are lifted from the Swift app so muscle memory survives the
 * move: a single-line field commits on Return, a description commits on
 * Command-Return, and Escape always abandons the edit.
 *
 * These are plain predicates over a keyboard-event *shape* rather than hooks or
 * handlers, so they can be unit-tested with object literals and reused by any
 * component that grows an editable field.
 */

/** The subset of `KeyboardEvent` these rules read. */
export type KeyChord = {
  key: string;
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
};

/** True while an IME composition is open — Enter is then a candidate pick. */
export function isComposing(event: { isComposing?: boolean; keyCode?: number }): boolean {
  // Safari fires the final `keydown` of a composition with `isComposing: false`
  // but the legacy 229 keyCode, so both have to be checked or the first Enter
  // after typing Japanese or Thai submits a half-finished word.
  return event.isComposing === true || event.keyCode === 229;
}

/**
 * Return with no modifiers — commits a single-line field.
 *
 * Shift-Return is deliberately excluded: it is the universal "newline anyway"
 * escape hatch, and a field that swallows it feels broken.
 */
export function isCommitChord(event: KeyChord): boolean {
  return isEnter(event) && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

/**
 * Command-Return (or Control-Return away from Apple hardware) — commits a
 * multi-line editor, where a bare Return has to keep inserting newlines.
 */
export function isMultilineCommitChord(event: KeyChord): boolean {
  return isEnter(event) && (event.metaKey === true || event.ctrlKey === true) && !event.altKey;
}

/** Escape — abandons the edit and restores the original value. */
export function isCancelChord(event: KeyChord): boolean {
  return event.key === "Escape" || event.key === "Esc";
}

/**
 * Return, with or without modifiers.
 *
 * Exported so no component has to spell the key name itself: `"Return"` is the
 * legacy `KeyboardEvent.key` value that some runtimes and automation drivers
 * still emit, and a hand-written `event.key === "Enter"` silently ignores it.
 */
export function isEnterKey(event: KeyChord): boolean {
  return isEnter(event);
}

function isEnter(event: KeyChord): boolean {
  return event.key === "Enter" || event.key === "Return";
}

/* -------------------------------------------------------------------------- */
/* Platform-dependent labelling                                                */
/* -------------------------------------------------------------------------- */

/**
 * Whether this is Apple hardware, which decides whether the hint reads `⌘` or
 * `Ctrl`. `userAgentData.platform` first, then the legacy `platform` string;
 * both are passed in so the function stays pure and testable.
 */
export function isApplePlatform(
  navigatorLike: { userAgent?: string; platform?: string } | undefined = typeof navigator ===
  "undefined"
    ? undefined
    : navigator,
): boolean {
  const haystack = `${navigatorLike?.platform ?? ""} ${navigatorLike?.userAgent ?? ""}`;
  return /mac|iphone|ipad|ipod/i.test(haystack);
}

/** `⌘↵` or `Ctrl ↵`, for the hint under a description editor. */
export function multilineCommitLabel(apple: boolean = isApplePlatform()): string {
  return apple ? "⌘↵" : "Ctrl ↵";
}

/* -------------------------------------------------------------------------- */
/* Draft rules                                                                 */
/* -------------------------------------------------------------------------- */

export type DraftRules = {
  /** Collapse surrounding whitespace before comparing and saving. */
  trim?: boolean;
  /** Allow committing an empty value (a description may be cleared). */
  allowEmpty?: boolean;
};

/** The value that would actually be written for a draft. */
export function normalizeDraft(draft: string, rules: DraftRules = {}): string {
  return rules.trim === false ? draft : draft.trim();
}

/**
 * Whether committing this draft is worth a request.
 *
 * An unchanged draft and — unless explicitly allowed — an emptied one are both
 * treated as "nothing to do", which is what turns a stray Return into a no-op
 * instead of a write that blanks a title.
 */
export function shouldCommitDraft(
  draft: string,
  original: string,
  rules: DraftRules = {},
): boolean {
  const next = normalizeDraft(draft, rules);
  if (next === normalizeDraft(original, rules)) {
    return false;
  }
  return rules.allowEmpty === true || next.length > 0;
}

/** A single-line field must never carry newlines, however the text arrived. */
export function collapseToSingleLine(value: string): string {
  return value.replace(/[\r\n]+/g, " ").replace(/\s{2,}/g, " ");
}

/**
 * Rows a textarea should show for its content, clamped to the caller's range.
 * Keeps the auto-grow behaviour testable independently of layout.
 */
export function autoGrowRows(value: string, minRows = 3, maxRows = 12): number {
  const lines = value.length === 0 ? 1 : value.split("\n").length;
  return Math.min(Math.max(lines, minRows), Math.max(minRows, maxRows));
}
